/**
 * Current Subscription 状态管理 Store
 *
 * 缓存当前用户的 `/subscriptions/current` 数据。由 `useAuthedBootstrap` 在登录态
 * 进入 Layout 时预拉一次，页面（如 Recharge）内可直接从 store 读取，
 * 避免每次进入页面时 `currentSubscription` 从 null 起步、导致"当前计划"
 * 在首屏短暂可点的 UX 抖动。
 */

import { create } from 'zustand';
import { getCurrentSubscription, type Subscription } from '@/api';

/**
 * CurrentSubscription 状态接口
 */
export interface CurrentSubscriptionState {
  // ==================== 状态 ====================

  /** 当前订阅；未订阅或未拉到时为 `null` */
  currentSubscription: Subscription | null;
  /** 是否正在请求 /subscriptions/current */
  isLoadingCurrentSubscription: boolean;
  /**
   * 是否已成功拉取过一次 /subscriptions/current。
   * 用于区分「未拉过」与「拉过但返回 null（无订阅）」两种状态，
   * 避免后续刷新时整块 UI 回到 skeleton。
   */
  currentSubscriptionLoaded: boolean;

  // ==================== Actions ====================

  /** 拉取 /subscriptions/current 并更新缓存 */
  refreshCurrentSubscription: () => Promise<void>;

  /** 直接覆盖 currentSubscription（用于 mutation 后的乐观更新） */
  setCurrentSubscription: (sub: Subscription | null) => void;

  /** 清空所有订阅数据；切换账号 / 登出时调用 */
  clear: () => void;
}

const initialState = {
  currentSubscription: null as Subscription | null,
  isLoadingCurrentSubscription: false,
  currentSubscriptionLoaded: false,
};

/**
 * Current Subscription Store
 *
 * 仅内存缓存接口数据，刷新页面后必须重新请求，避免展示过期业务数据。
 */
export const useCurrentSubscriptionStore = create<CurrentSubscriptionState>()((set) => ({
  ...initialState,

  refreshCurrentSubscription: async () => {
    set({ isLoadingCurrentSubscription: true });
    try {
      const res = await getCurrentSubscription();
      if (res.ok) {
        set({
          currentSubscription: res.data,
          currentSubscriptionLoaded: true,
        });
      } else {
        console.error(
          '[CurrentSubscription Store] getCurrentSubscription failed:',
          res.error
        );
      }
    } catch (error) {
      console.error(
        '[CurrentSubscription Store] getCurrentSubscription error:',
        error
      );
    } finally {
      set({ isLoadingCurrentSubscription: false });
    }
  },

  setCurrentSubscription: (sub) => {
    set({ currentSubscription: sub, currentSubscriptionLoaded: true });
  },

  clear: () => {
    set(initialState);
  },
}));

/**
 * 便捷 Hooks
 */

/** 当前订阅；未订阅或未拉到时为 null */
export const useCurrentSubscription = () =>
  useCurrentSubscriptionStore((state) => state.currentSubscription);

/** 是否正在请求 /subscriptions/current */
export const useCurrentSubscriptionLoading = () =>
  useCurrentSubscriptionStore((state) => state.isLoadingCurrentSubscription);

/** 是否已成功拉取过一次 /subscriptions/current */
export const useCurrentSubscriptionLoaded = () =>
  useCurrentSubscriptionStore((state) => state.currentSubscriptionLoaded);

/** 获取 refreshCurrentSubscription action */
export const useRefreshCurrentSubscription = () =>
  useCurrentSubscriptionStore((state) => state.refreshCurrentSubscription);

/** 获取 setCurrentSubscription action */
export const useSetCurrentSubscription = () =>
  useCurrentSubscriptionStore((state) => state.setCurrentSubscription);
